<template>
    <div class="flex flex-col border border-gray-200">
        <div class="calendar-header flex py-2 border-b border-gray-200">
            <div
                class="cursor-pointer flex items-center justify-center px-4"
                @click="gotoPrevMonth"
            >
                <IconChevronLeft />
            </div>

            <div class="grow flex justify-center font-bold">
                {{ monthName }} {{ selectedYear }}
            </div>

            <div
                class="cursor-pointer flex items-center justify-center px-4"
                @click="gotoNextMonth"
            >
                <IconChevronRight />
            </div>
        </div>

        <div class="calendar-body grid grid-cols-7">
            <div
                v-for="(d, i) in weekDays"
                :key="'wd-' + i"
                class="flex justify-center items-center"
            >
                {{ d }}
            </div>

            <template v-if="emptyDays.length">
                <div
                    v-for="d in emptyDays"
                    :key="'empty-' + d"
                    class="flex justify-center items-center"
                >
          <span class="w-8 h-8 flex justify-center items-center p-2 text-gray-300">
            &nbsp;
          </span>
                </div>
            </template>

            <template
v-for="(day, index) in daysOfMonth"
:key="'day-' + index"
>
                <div
                    v-if="!isWeekend(day) && !isPast(day)"
                    class="cursor-pointer flex justify-center items-center"
                    @click="setSelectedDate(index + 1)"
                >
          <span
              class="w-8 h-8 flex justify-center items-center p-2 rounded-full hover:bg-gray-400 hover:text-white"
              :class="selectedDate === index + 1 && !deactivateSelection ? 'bg-black text-white' : ''"
          >
            {{ index + 1 }}
          </span>
                </div>

                <div
v-else
class="flex justify-center items-center"
>
          <span class="w-8 h-8 flex justify-center items-center p-2 text-gray-300">
            {{ index + 1 }}
          </span>
                </div>
            </template>
        </div>
    </div>
</template>

<script setup lang="ts">
import IconChevronLeft from '~/components/Icons/IconChevronLeft.vue';
import IconChevronRight from '~/components/Icons/IconChevronRight.vue';

const props = withDefaults(defineProps<{ deactivateSelection?: boolean }>(), {
    deactivateSelection: false,
});

const emit = defineEmits<{(e: 'selected', date: Date): void
}>();

const weekDays = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

const today = new Date();
const currentDay = today.getDate();
const currentMonth = today.getMonth() + 1;
const currentYear = today.getFullYear();

const selectedDate = ref(0);
const selectedMonth = ref(currentMonth);
const selectedYear = ref(currentYear);

const emptyDays = ref<number[]>([]);
const daysOfMonth = ref<Date[]>([]);

function getAllDaysOfMonth(month: number, year: number) {
    const date = new Date(year, month - 1, 1);
    const days: Date[] = [];

    while (date.getMonth() === month - 1) {
        days.push(new Date(date));
        date.setDate(date.getDate() + 1);
    }

    // 0=So,1=Mo,... => wir wollen Mo als Start; So soll 7 sein
    let indexOfFirstDay = days[0]?.getDay() ?? 1;
    if (indexOfFirstDay === 0) indexOfFirstDay = 7;

    const empties: number[] = [];
    for (let i = 1; i < indexOfFirstDay; i++) empties.push(i);

    emptyDays.value = empties;
    return days;
}

const monthName = computed(() => {
    const d = new Date(selectedYear.value, selectedMonth.value - 1, 1);
    return d.toLocaleString('de-DE', {month: 'long'});
});

function gotoPrevMonth() {
    selectedMonth.value -= 1;
    if (selectedMonth.value < 1) {
        selectedMonth.value = 12;
        selectedYear.value -= 1;
    }
    daysOfMonth.value = getAllDaysOfMonth(selectedMonth.value, selectedYear.value);
    selectedDate.value = 0;
}

function gotoNextMonth() {
    selectedMonth.value += 1;
    if (selectedMonth.value > 12) {
        selectedMonth.value = 1;
        selectedYear.value += 1;
    }
    daysOfMonth.value = getAllDaysOfMonth(selectedMonth.value, selectedYear.value);
    selectedDate.value = 0;
}

function isWeekend(date: Date) {
    return date.getDay() === 0 || date.getDay() === 6;
}

function isPast(date: Date) {
    // „Vergangenheit“ (inkl. heute früher) – entspricht deinem Nuxt2-Verhalten
    // Wenn du "heute erlauben" willst: compare nur Datum ohne Uhrzeit
    return date < new Date();
}

function setSelectedDate(dayIndex: number) {
    selectedDate.value = dayIndex;
    const d = new Date(selectedYear.value, selectedMonth.value - 1, dayIndex);
    emit('selected', d);
}

onMounted(() => {
    daysOfMonth.value = getAllDaysOfMonth(currentMonth, currentYear);
    selectedMonth.value = currentMonth;
    selectedYear.value = currentYear;
    selectedDate.value = currentDay;
});
</script>
